/**
 * /admin/nachweise — Nachweise & Pflichten.
 *
 * Every tenancy owes three things: a registration of residence, private
 * liability cover and a deposit. This page is the one place that lists all of
 * them at once and says, per row, where each one stands.
 *
 * THE BACKEND DECIDES; THIS PAGE ASKS AND RENDERS. Status, effective deadline,
 * document counts and the six figures above the table are all fields of a single
 * GET. Nothing here compares a date against today, counts documents to conclude
 * a requirement is met, or adds up rows to produce a total. That work lives in
 * the requirement engine, which sees every tenancy and the Swiss business day;
 * a browser sees one page of rows and its own clock.
 *
 * THE CARDS AND THE LIST ARE TWO DIFFERENT POPULATIONS, and that is the point.
 * `summary` counts everything the request selects EXCEPT the status filter —
 * organisation, search, requirement type and whether suspended rows are shown.
 * `items`/`total` then apply status on top. So pressing "Überfällig" narrows the
 * table while the other five cards keep their numbers and stay usable as
 * navigation; typing a search term, however, moves all six, because search is
 * part of the base population. Re-deriving either figure locally would collapse
 * that distinction into one wrong number.
 *
 * EVERY FILTER IS SERVER-SIDE, and every change to one starts again at the first
 * page. Filtering rows already on screen would silently mean "filter within the
 * current page", which is a different answer that looks like the same one.
 *
 * AFTER A WRITE, THE WHOLE LIST IS RELOADED. An override endpoint answers with
 * what it stored — a deadline, a waiver, nothing else. It cannot say what the
 * row's status becomes, because that is derived, so patching the response into
 * the row on screen would invent the half it does not carry.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlarmClock,
  CheckCircle2,
  Clock,
  ListChecks,
  MinusCircle,
  PauseCircle,
} from "lucide-react";
import { toast } from "sonner";

import { fetchAdminTenancyRequirements, unwaiveRequirement } from "../../api/adminData";
import BexioKpiFilterCard from "../../components/admin/bexio/workspace/BexioKpiFilterCard";
import BexioTablePagination from "../../components/admin/bexio/workspace/BexioTablePagination";
import {
  BexioTableError,
  BexioTableSkeleton,
} from "../../components/admin/bexio/workspace/BexioTableStates";
import RequirementDetailDrawer from "./nachweise/RequirementDetailDrawer";
import RequirementDueDateModal from "./nachweise/RequirementDueDateModal";
import RequirementFilters from "./nachweise/RequirementFilters";
import RequirementWaiveModal from "./nachweise/RequirementWaiveModal";
import RequirementsTable, { rowKey } from "./nachweise/RequirementsTable";
import { REQUIREMENT_FACETS } from "./nachweise/requirementLabels";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 50;

/** Card icons, chosen here rather than in the label map: that module is the
 *  vocabulary and has no business importing an icon set. */
const FACET_ICONS = {
  total: ListChecks,
  overdue: AlarmClock,
  open: Clock,
  fulfilled: CheckCircle2,
  suspended: PauseCircle,
  not_required: MinusCircle,
};

const EMPTY = {
  loading: true,
  error: null,
  items: [],
  total: 0,
  summary: null,
};

export default function AdminTenancyRequirementsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [requirementType, setRequirementType] = useState("");
  const [includeSuspended, setIncludeSuspended] = useState(false);
  const [status, setStatus] = useState(null);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [skip, setSkip] = useState(0);

  const [data, setData] = useState(EMPTY);

  // Which row has a write in flight, as `tenancy:type`. One at a time — the
  // menus are per row and a second action cannot be started while the first is
  // still open.
  const [busyKey, setBusyKey] = useState(null);
  const [dueDateRow, setDueDateRow] = useState(null);
  const [waiveRow, setWaiveRow] = useState(null);
  const [waiveMode, setWaiveMode] = useState("waive");

  // Which requirement the drawer is showing, as `tenancy_id × requirement_type`
  // — the row's identity, not the row. Holding the OBJECT would freeze a copy
  // that a reload silently makes stale: after documenting a contact the list
  // comes back with a new `last_requested_at`, and a drawer still rendering the
  // captured row would show the old one beside a list showing the new one.
  const [selectedKey, setSelectedKey] = useState(null);

  // Guards a late response from a superseded query overwriting a newer one.
  const requestSeq = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any change to what is being asked for starts again at the first page.
  useEffect(() => {
    setSkip(0);
  }, [search, requirementType, includeSuspended, status, limit]);

  const load = useCallback(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setData((prev) => ({ ...prev, loading: true, error: null }));

    return fetchAdminTenancyRequirements({
      status: status || undefined,
      requirementType: requirementType || undefined,
      search: search || undefined,
      includeSuspended,
      skip,
      limit,
    })
      .then((res) => {
        if (requestSeq.current !== seq) return;
        setData({
          loading: false,
          error: null,
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total || 0),
          summary: res?.summary || null,
        });
      })
      .catch((e) => {
        if (requestSeq.current !== seq) return;
        setData({
          loading: false,
          error: e?.message || "Nachweise konnten nicht geladen werden.",
          items: [],
          total: 0,
          summary: null,
        });
      });
  }, [status, requirementType, search, includeSuspended, skip, limit]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * A write can empty the page you are standing on — waive the last overdue row
   * while the Überfällig facet is active and page 3 of 3 stops existing.
   *
   * The correction is `BexioTablePagination`'s own clamp, applied to the state
   * the component only clamps for display: the last page starts at
   * `(ceil(total/limit) - 1) * limit`. No guess about why the page emptied, and
   * nothing happens while a request is in flight or when there is simply
   * nothing to show.
   */
  useEffect(() => {
    if (data.loading || data.error) return;
    if (data.total <= 0 || skip < data.total) return;
    const lastPageStart = (Math.max(1, Math.ceil(data.total / limit)) - 1) * limit;
    if (lastPageStart !== skip) setSkip(lastPageStart);
  }, [data.loading, data.error, data.total, skip, limit]);

  const filtered = Boolean(search || requirementType || status || includeSuspended);

  const reset = useCallback(() => {
    setSearchInput("");
    setSearch("");
    setRequirementType("");
    setIncludeSuspended(false);
    setStatus(null);
  }, []);

  const selectFacet = useCallback((next) => setStatus(next), []);

  const closeModals = useCallback(() => {
    setDueDateRow(null);
    setWaiveRow(null);
  }, []);

  const onSaved = useCallback(() => {
    closeModals();
    load();
  }, [closeModals, load]);

  const handleUnwaive = useCallback(
    async (row) => {
      setBusyKey(rowKey(row));
      try {
        await unwaiveRequirement(row.tenancy_id, row.requirement_type);
        toast.success("Erlass aufgehoben.");
        load();
      } catch (e) {
        toast.error(e?.message || "Der Erlass konnte nicht aufgehoben werden.");
      } finally {
        setBusyKey(null);
      }
    },
    [load]
  );

  const actions = useMemo(
    () => ({
      onEditDueDate: (row) => setDueDateRow(row),
      onWaive: (row) => {
        setWaiveMode("waive");
        setWaiveRow(row);
      },
      onEditReason: (row) => {
        setWaiveMode("reason");
        setWaiveRow(row);
      },
      onUnwaive: handleUnwaive,
    }),
    [handleUnwaive]
  );

  // Resolved out of the CURRENT items on every render, so the drawer always
  // shows the freshest version of the row it was opened on. Gone from the page
  // — filtered away, or paged past — closes it rather than showing a ghost.
  const selectedRow = useMemo(
    () =>
      selectedKey ? data.items.find((r) => rowKey(r) === selectedKey) || null : null,
    [selectedKey, data.items]
  );

  const openDetails = useCallback((row) => setSelectedKey(rowKey(row)), []);
  const closeDetails = useCallback(() => setSelectedKey(null), []);

  const resultLabel = data.loading
    ? null
    : `${data.total} ${data.total === 1 ? "Eintrag" : "Einträge"}`;

  return (
    <div className="mx-auto flex max-w-[1500px] flex-col gap-4 px-6 py-6">
      <header>
        <h1 className="m-0 text-[24px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
          Nachweise &amp; Pflichten
        </h1>
        <p className="m-0 mt-[4px] text-[12.5px] leading-snug text-muted-foreground">
          Überblick über offene, überfällige und erledigte Nachweise und Pflichten.
        </p>
      </header>

      {/* Six cards, six numbers, all of them the backend's. A card shows "—"
          while a request is in flight rather than the previous answer: the
          figures belong to a filter set, and holding them across a change would
          show counts for a question that is no longer being asked. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        {REQUIREMENT_FACETS.map((facet) => (
          <BexioKpiFilterCard
            key={facet.key}
            icon={FACET_ICONS[facet.key]}
            tone={facet.tone}
            label={facet.label}
            value={
              data.loading || !data.summary ? "—" : (data.summary[facet.key] ?? 0)
            }
            active={status === facet.status}
            onSelect={() => selectFacet(facet.status)}
          />
        ))}
      </div>

      <RequirementFilters
        search={searchInput}
        onSearchChange={setSearchInput}
        requirementType={requirementType}
        onRequirementTypeChange={setRequirementType}
        includeSuspended={includeSuspended}
        onIncludeSuspendedChange={setIncludeSuspended}
        status={status}
        onStatusChange={setStatus}
        onReset={reset}
        canReset={filtered}
        resultLabel={resultLabel}
      />

      <div className="rounded-xl border border-border bg-card">
        {data.error ? (
          <BexioTableError message={data.error} onRetry={load} />
        ) : data.loading ? (
          <div data-testid="requirements-loading">
            <BexioTableSkeleton rows={6} columns={8} />
          </div>
        ) : data.items.length === 0 ? (
          <p
            data-testid="requirements-empty"
            className="m-0 px-6 py-12 text-center text-[13px] text-muted-foreground"
          >
            {filtered
              ? "Keine Nachweise entsprechen den aktuellen Filtern."
              : "Keine Nachweise vorhanden."}
          </p>
        ) : (
          <RequirementsTable
            rows={data.items}
            actions={actions}
            busyKey={busyKey}
            onOpenDetails={openDetails}
          />
        )}

        {!data.loading && !data.error ? (
          <BexioTablePagination
            total={data.total}
            offset={skip}
            limit={limit}
            onOffsetChange={setSkip}
            onLimitChange={setLimit}
            noun="Nachweise"
          />
        ) : null}
      </div>

      {/* The workspace drawer DOES offer "Mieter öffnen" — the reader is not on
          that person's page. It offers no documents link: no URL contract opens
          the tenant's Dokumente tab, and inventing one would be a promise the
          other page does not keep. */}
      <RequirementDetailDrawer
        row={selectedRow}
        onClose={closeDetails}
        onRequirementRefresh={load}
        actions={actions}
      />

      <RequirementDueDateModal
        row={dueDateRow}
        onClose={closeModals}
        onSaved={onSaved}
      />
      <RequirementWaiveModal
        row={waiveRow}
        mode={waiveMode}
        onClose={closeModals}
        onSaved={onSaved}
      />
    </div>
  );
}
